import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { ArrowRight, ArrowUpRight, LayoutTemplate, Moon, PanelLeft, Sun } from 'lucide-react';
import { Button } from '../shared/Button.jsx';
import { ToggleGroup, ToggleGroupItem } from '../shared/ToggleGroup.jsx';
import { Sidebar as ShellFrame, SidebarInset, SidebarRail } from '../shared/Sidebar.jsx';
import { BrandLogo } from './Logo.jsx';
import { Sidebar } from './Sidebar.jsx';
import { StoryView } from './StoryView.jsx';
import { Home } from './Home.jsx';
import { Compose } from './Compose.jsx';
import { Search } from './Search.jsx';
import { createBridge } from './bridge.js';
import { readUrl, writeUrl } from './url.js';
import { buildTree, ancestorsOf } from './tree.js';
import { createPluginHost } from './plugins.js';
import { Segments } from './glide.jsx';

const bridge = createBridge();
// For debugging and for tools that drive the shell (parity checks, tests).
window.__EDL_DOCS_BRIDGE__ = bridge;

function reducer(state, action) {
  switch (action.type) {
    case 'index':
      return { ...state, index: action.index };
    case 'select':
      return { ...state, id: action.id, viewMode: action.viewMode, page: action.page ?? null, story: action.keepStory ? state.story : null, status: action.id ? 'loading' : 'idle', error: null };
    case 'globals':
      return { ...state, globals: { ...state.globals, ...action.globals } };
    case 'storyPrepared':
      if (action.msg.id !== state.id) return state;
      return { ...state, story: { argTypes: action.msg.argTypes, args: action.msg.args, initialArgs: action.msg.initialArgs, parameters: action.msg.parameters, name: action.msg.name, title: action.msg.title, server: action.msg.server } };
    case 'args':
      if (!state.story) return state;
      return { ...state, story: { ...state.story, args: { ...state.story.args, ...action.args } } };
    case 'resetArgs':
      if (!state.story) return state;
      return { ...state, story: { ...state.story, args: { ...state.story.initialArgs } } };
    case 'rendered':
      if (action.msg.id !== state.id) return state;
      return { ...state, status: 'rendered', error: null, story: state.story && action.msg.server ? { ...state.story, server: true } : state.story };
    case 'error':
      if (action.msg.id !== state.id) return state;
      return { ...state, status: 'error', error: action.msg };
    case 'tick':
      return { ...state, tick: state.tick + 1 };
    default:
      return state;
  }
}

export function App({ boot }) {
  const { config } = boot;
  const initialUrl = useMemo(readUrl, []);
  const defaultGlobals = Object.fromEntries(Object.entries(config.globals ?? {}).map(([k, v]) => [k, v.default]));
  const [state, dispatch] = useReducer(reducer, {
    index: boot.index,
    id: initialUrl.id ?? config.defaultStory ?? null,
    viewMode: initialUrl.viewMode ?? null,
    page: initialUrl.id ? null : initialUrl.page,
    globals: { ...defaultGlobals, ...readStoredGlobals(), ...initialUrl.globals },
    story: null,
    status: initialUrl.id ? 'loading' : 'idle',
    error: null,
    tick: 0,
  });
  const [searchOpen, setSearchOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(() => window.matchMedia('(min-width: 901px)').matches);
  const urlArgs = useRef(initialUrl.args);
  const docsRestore = useRef(initialUrl.viewMode === 'docs' ? (initialUrl.hash ? { anchor: initialUrl.hash } : null) : null);
  const viewportRef = useRef(initialUrl.hasViewport ? initialUrl.viewport : null);
  const [urlViewport, setUrlViewport] = useState({
    value: initialUrl.viewport,
    explicit: initialUrl.hasViewport,
    key: 0,
  });
  const stateRef = useRef(state);
  stateRef.current = state;

  const entry = state.id ? state.index.entries[state.id] : null;
  const tree = useMemo(() => buildTree(state.index), [state.index]);

  // `args` and `globals` open the story in a given state (an answer by meaning).
  const navigate = useCallback((target, { replace = false, page = null, args = null, globals = null } = {}) => {
    const id = typeof target === 'string' ? target : target?.id ?? null;
    const viewMode = id ? (id.endsWith('--docs') ? 'docs' : 'story') : null;
    const hash = typeof target === 'object' ? target?.hash ?? '' : '';
    const targetArgs = viewMode === 'story' && typeof target === 'object' ? target?.args ?? null : null;
    const given = args ?? targetArgs;
    const withArgs = id && given && Object.keys(given).length ? given : null;
    const allGlobals = globals ? { ...stateRef.current.globals, ...globals } : stateRef.current.globals;
    if (globals && Object.keys(globals).length) dispatch({ type: 'globals', globals });
    const same = id === stateRef.current.id;
    if (stateRef.current.viewMode === 'docs' && typeof target === 'object' && (Number.isFinite(target?.docsScrollY) || target?.originStoryId)) {
      history.replaceState(
        {
          ...(history.state ?? {}),
          edlDocsRestore: {
            id: stateRef.current.id,
            scrollY: Number.isFinite(target.docsScrollY) ? target.docsScrollY : undefined,
            anchor: target.originStoryId ?? undefined,
          },
        },
        '',
        location.href,
      );
    }
    urlArgs.current = withArgs;
    docsRestore.current = viewMode === 'docs' ? (hash ? { anchor: hash } : { top: true }) : null;
    dispatch({ type: 'select', id, viewMode, page: id ? null : page });
    writeUrl({
      id,
      viewMode,
      page: id ? null : page,
      globals: nonDefault(allGlobals, defaultGlobals),
      args: withArgs,
      viewport: viewMode === 'story' ? viewportRef.current : null,
      hash,
    }, { replace });
    // The same story again: no selection change to send it, so send it here, with only these args.
    if (same && withArgs && bridge.isReady()) {
      bridge.send({ type: 'resetArgs', id });
      bridge.send({ type: 'select', id, viewMode, globals: allGlobals, index: stateRef.current.index, args: withArgs });
      urlArgs.current = null;
    }
    if (window.matchMedia('(max-width: 900px)').matches) setSidebarOpen(false);
  }, []);

  const pluginsRef = useRef(null);
  if (!pluginsRef.current) {
    pluginsRef.current = createPluginHost({
      getState: () => ({ ...stateRef.current, entry: stateRef.current.id ? stateRef.current.index.entries[stateRef.current.id] : null, config }),
      navigate,
      refresh: () => dispatch({ type: 'tick' }),
    });
  }
  const plugins = pluginsRef.current;

  useEffect(() => {
    plugins.load(boot.plugins ?? []);
  }, []);

  // Preview → shell
  useEffect(
    () =>
      bridge.onMessage((msg) => {
        if (msg.type === 'ready') sendSelect();
        else if (msg.type === 'storyPrepared') {
          dispatch({ type: 'storyPrepared', msg });
          if (msg.id === stateRef.current.id && stateRef.current.viewMode === 'story') {
            writeUrl({
              id: msg.id,
              viewMode: 'story',
              globals: nonDefault(stateRef.current.globals, defaultGlobals),
              args: diffArgs(msg.args, msg.initialArgs),
              viewport: viewportRef.current,
            }, { replace: true });
          }
        }
        else if (msg.type === 'rendered') dispatch({ type: 'rendered', msg });
        else if (msg.type === 'error') dispatch({ type: 'error', msg });
        else if (msg.type === 'navigate') navigate(msg);
      }),
    [],
  );

  // Shell → preview: the selection, with any args changed in the panel so a
  // preview reload lands where the user was.
  function sendSelect() {
    const s = stateRef.current;
    if (!s.id) return;
    const changedArgs = s.story ? diffArgs(s.story.args, s.story.initialArgs) : null;
    const args = { ...(urlArgs.current ?? {}), ...(changedArgs ?? {}) };
    if (!bridge.isReady()) return;
    bridge.send({
      type: 'select',
      id: s.id,
      viewMode: s.viewMode,
      globals: s.globals,
      index: s.index,
      args: Object.keys(args).length ? args : undefined,
      docsRestore: s.viewMode === 'docs' ? docsRestore.current ?? undefined : undefined,
    });
    urlArgs.current = null;
    docsRestore.current = null;
  }

  useEffect(() => {
    if (!state.id) return;
    sendSelect();
    plugins.emit('select', { id: state.id, entry });
  }, [state.id]);

  useEffect(() => {
    bridge.send({ type: 'setGlobals', globals: state.globals });
    // A project may name its themes "Light" / "Dark"; the shell's CSS keys on lowercase.
    const theme = String(state.globals.theme ?? '').toLowerCase();
    document.documentElement.dataset.previewTheme = theme;
    // The inset is themed. The page around it (the rail and the gutter) has
    // to be too, or it stays on :root's --edl-bg, which is black.
    if (theme) document.documentElement.dataset.theme = theme;
    else delete document.documentElement.dataset.theme;
    try {
      localStorage.setItem('edl-docs:globals', JSON.stringify(state.globals));
    } catch {}
  }, [JSON.stringify(state.globals)]);

  // Live index (dev only): story files added, removed or renamed.
  useEffect(() => {
    if (config.mode !== 'dev' || typeof EventSource === 'undefined') return undefined;
    const es = new EventSource('/__edl/events');
    es.addEventListener('index', (e) => {
      const index = JSON.parse(e.data);
      dispatch({ type: 'index', index });
      bridge.send({ type: 'index', index });
    });
    return () => es.close();
  }, []);

  // Back / forward
  useEffect(() => {
    const onPop = (event) => {
      const u = readUrl();
      urlArgs.current = u.args;
      docsRestore.current = u.viewMode === 'docs'
        ? event.state?.edlDocsRestore ?? (u.hash ? { anchor: u.hash } : { top: true })
        : null;
      setUrlViewport((current) => ({
        value: u.viewport,
        explicit: u.hasViewport,
        key: current.key + 1,
      }));
      dispatch({ type: 'select', id: u.id, viewMode: u.viewMode, page: u.id ? null : u.page });
      if (Object.keys(u.globals).length) dispatch({ type: 'globals', globals: u.globals });
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // ⌘K toggles search. "/" opens it, and never while typing.
  useEffect(() => {
    const onKey = (e) => {
      const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement?.tagName ?? '') || document.activeElement?.isContentEditable;
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setSearchOpen((open) => !open);
      } else if (e.key === '/' && !typing) {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    document.title = entry ? `${entry.title.split('/').at(-1)}${entry.type === 'story' ? ` · ${entry.name}` : ''} · ${config.title}` : `${config.title} · ${config.brand?.name ? `${config.brand.name} docs` : 'edl docs'}`;
  }, [entry?.id]);

  const setGlobals = (globals) => {
    const nextGlobals = { ...state.globals, ...globals };
    dispatch({ type: 'globals', globals });
    writeUrl({
      id: state.id,
      viewMode: state.viewMode,
      page: state.page,
      globals: nonDefault(nextGlobals, defaultGlobals),
      args: state.story ? diffArgs(state.story.args, state.story.initialArgs) : urlArgs.current,
      viewport: state.viewMode === 'story' ? viewportRef.current : null,
      hash: state.viewMode === 'docs' ? readUrl().hash : '',
    }, { replace: true });
  };

  const updateArgs = (args) => {
    const nextArgs = state.story ? { ...state.story.args, ...args } : args;
    dispatch({ type: 'args', args });
    bridge.send({ type: 'updateArgs', id: state.id, args });
    writeUrl({
      id: state.id,
      viewMode: state.viewMode,
      globals: nonDefault(state.globals, defaultGlobals),
      args: diffArgs(nextArgs, state.story?.initialArgs),
      viewport: viewportRef.current,
    }, { replace: true });
  };
  const resetArgs = () => {
    dispatch({ type: 'resetArgs' });
    bridge.send({ type: 'resetArgs', id: state.id });
    writeUrl({
      id: state.id,
      viewMode: state.viewMode,
      globals: nonDefault(state.globals, defaultGlobals),
      viewport: viewportRef.current,
    }, { replace: true });
  };

  const setViewport = (viewport) => {
    viewportRef.current = viewport;
    if (stateRef.current.viewMode !== 'story') return;
    const s = stateRef.current;
    writeUrl({
      id: s.id,
      viewMode: s.viewMode,
      globals: nonDefault(s.globals, defaultGlobals),
      args: s.story ? diffArgs(s.story.args, s.story.initialArgs) : urlArgs.current,
      viewport,
    }, { replace: true });
  };

  const openAncestors = ancestorsOf(entry);

  return (
    <div className={`shell${sidebarOpen ? ' is-sidebar-open' : ''}`} data-theme={String(state.globals.theme ?? '').toLowerCase() || undefined}>
      {entry?.type === 'story' && <a className="ws-skip" href="#canvas-preview">Skip to canvas</a>}
      <ShellFrame variant="inset" className="shell-frame">
        <SidebarRail>
          <div className="shell-brand">
            <a
              className="shell-top__logo"
              href="./"
              onClick={(e) => {
                e.preventDefault();
                navigate(null);
              }}
              aria-label={`${config.title} home`}
            >
              <BrandLogo brand={config.brand} height={config.brand?.logo ? 20 : 24} />
            </a>
            {/* company | client: an agency's docs name the client beside the
                agency's logo; a direct client's title is their own name, which
                the logo already says. */}
            {sameName(config.title, config.brand?.name) ? null : (
              <>
                <span className="shell-top__divider" aria-hidden="true" />
                <span className="shell-top__project">{config.title}</span>
              </>
            )}
          </div>
          <Sidebar tree={tree} activeId={state.id} openKeys={openAncestors} onSelect={navigate} badgesFor={plugins.badgesFor} index={state.index} />
        </SidebarRail>
        <SidebarInset data-theme={String(state.globals.theme ?? '').toLowerCase() || undefined}>
          <main className="shell-main" id="main">
            <div className={`shell-main__bar${searchOpen ? ' is-search-open' : ''}`}>
              <Button
                variant="ghost"
                className="shell-sidebar-toggle"
                leadingIcon={PanelLeft}
                aria-label={sidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
                tooltip={sidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
                aria-expanded={sidebarOpen}
                onClick={() => setSidebarOpen((open) => !open)}
              />
              <Search
                index={state.index}
                ask={Boolean(config.ask)}
                globals={state.globals}
                open={searchOpen}
                onOpen={() => setSearchOpen(true)}
                onClose={() => setSearchOpen(false)}
                onSelect={(id, state) => {
                  setSearchOpen(false);
                  navigate(id, { args: state?.args, globals: state?.globals });
                }}
              />
              <div className="shell-top__actions">
                {config.ask && (
                  <Button
                    variant="outlined"
                    className="shell-top__link"
                    leadingIcon={LayoutTemplate}
                    aria-current={state.page === 'compose' ? 'page' : undefined}
                    onClick={() => navigate(null, { page: 'compose' })}
                  >
                    Compose
                  </Button>
                )}
                {Object.entries(config.globals ?? {}).map(([key, g]) =>
                  key === 'theme' ? (
                    <ThemeToggle key={key} value={state.globals[key]} options={g.options} onChange={(v) => setGlobals({ [key]: v })} />
                  ) : (
                    <Segments key={key} data-global={key} className="shell-segmented" label={g.label} value={state.globals[key]} options={g.options} onChange={(v) => setGlobals({ [key]: v })} />
                  ),
                )}
                {(config.links ?? []).map((l) => (
                  <Button key={l.href} variant="outlined" className="shell-top__link" trailingIcon={ArrowUpRight} render={<a href={l.href} target="_blank" rel="noreferrer" />}>
                    {l.label}
                  </Button>
                ))}
              </div>
            </div>
            {state.page === 'compose' && !state.id ? (
              <Compose index={state.index} config={config} globals={state.globals} onNavigate={navigate} />
            ) : (
            <StoryView
              bridge={bridge}
              entry={entry}
              mode={!state.id ? 'home' : !entry ? 'missing' : entry.type === 'docs' ? 'docs' : 'story'}
              state={state}
              config={config}
              onArgs={updateArgs}
              onResetArgs={resetArgs}
              onGlobals={setGlobals}
              onNavigate={navigate}
              urlViewport={urlViewport}
              onViewport={setViewport}
              plugins={plugins}
            >
              {!state.id && <Home config={config} index={state.index} tree={tree} onNavigate={navigate} onSearch={() => setSearchOpen(true)} />}
              {state.id && !entry && <NotFound id={state.id} onHome={() => navigate(null)} />}
            </StoryView>
            )}
          </main>
        </SidebarInset>
      </ShellFrame>
    </div>
  );
}

// The toggle shows light and dark, and sends the project's own value for
// each ("Dark" in Axon, whose preview only knows that spelling).
function ThemeToggle({ value, options, onChange }) {
  const theme = String(value ?? '').toLowerCase() === 'dark' ? 'dark' : 'light';
  const valueFor = (want) => (options ?? []).map((o) => (o && typeof o === 'object' ? o.value : o)).find((v) => String(v).toLowerCase() === want) ?? want;
  return (
    <ToggleGroup
      data-global="theme"
      aria-label="Theme"
      tooltips={false}
      value={[theme]}
      onValueChange={(next) => { if (next[0] === 'light' || next[0] === 'dark') onChange(valueFor(next[0])); }}
    >
      <ToggleGroupItem value="light" icon={Sun} aria-label="Light" />
      <ToggleGroupItem value="dark" icon={Moon} aria-label="Dark" />
    </ToggleGroup>
  );
}

function NotFound({ id, onHome }) {
  return (
    <div className="shell-empty">
      <p className="edl-label shell-empty__label">Not found</p>
      <h1 className="shell-empty__title">Nothing lives at “{id}” any more.</h1>
      <p className="shell-empty__text">The canvas may have been renamed or removed.</p>
      <Button trailingIcon={ArrowRight} onClick={onHome}>
        Back to the overview
      </Button>
    </div>
  );
}

function diffArgs(args = {}, initial = {}) {
  const out = {};
  for (const [k, v] of Object.entries(args)) {
    if (v && typeof v === 'object' && v.__edlUnserialisable) continue;
    if (JSON.stringify(v) !== JSON.stringify(initial[k])) out[k] = v;
  }
  return Object.keys(out).length ? out : null;
}

function nonDefault(globals, defaults) {
  return Object.fromEntries(Object.entries(globals).filter(([k, v]) => defaults[k] !== v));
}

function readStoredGlobals() {
  try {
    return JSON.parse(localStorage.getItem('edl-docs:globals') ?? '{}') ?? {};
  } catch {
    return {};
  }
}

// "Accutics" and " accutics " are the same company.
function sameName(a, b) {
  return Boolean(a && b) && a.trim().toLowerCase() === b.trim().toLowerCase();
}
